.root {
  position: relative;
  min-width: 0;
  font-size: 15px;
  letter-spacing: normal;
}

.trigger {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 14px 0 16px;
  border-radius: var(--radius-field);
  background: var(--surface);
  border: 1px solid var(--line);
  color: var(--text);
  font: inherit;
  font-weight: 400;
  cursor: pointer;
  user-select: none;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.trigger:hover {
  border-color: var(--accent-deep);
}

.trigger:focus-visible,
.open .trigger {
  border-color: var(--accent);
  box-shadow: 0 0 0 4px var(--glow);
}

.value {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.placeholder {
  color: var(--muted-dim);
  font-weight: 300;
}

.chevron {
  flex: none;
  width: 18px;
  height: 18px;
  color: var(--accent);
  transition: transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.open .chevron {
  transform: rotate(180deg);
}

.invalid .trigger {
  border-color: var(--danger);
}

.disabled .trigger {
  opacity: 0.45;
  cursor: default;
  pointer-events: none;
}

.sm {
  font-size: 13px;
}

.sm .trigger {
  height: 38px;
  padding: 0 10px 0 12px;
  border-radius: 11px;
}

.sm .chevron {
  width: 15px;
  height: 15px;
}

.floating {
  position: fixed;
  z-index: 90;
  font-size: 15px;
  letter-spacing: normal;
  pointer-events: none;
}

.floating.sm {
  font-size: 13px;
}

.popup {
  position: absolute;
  pointer-events: auto;
  z-index: 40;
  left: 0;
  right: auto;
  min-width: 100%;
  width: max-content;
  max-width: min(420px, calc(100vw - 32px));
  top: calc(100% + 8px);
  padding: 6px;
  border-radius: 18px;
  background: color-mix(in srgb, var(--bg-raised) 94%, transparent);
  border: 1px solid var(--bar-line);
  box-shadow: var(--bar-shadow);
  -webkit-backdrop-filter: blur(18px) saturate(1.3);
  backdrop-filter: blur(18px) saturate(1.3);
  transform-origin: top center;
  animation: pop-in 0.18s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.up {
  top: auto;
  bottom: calc(100% + 8px);
  transform-origin: bottom center;
}

.sm .popup {
  padding: 4px;
  border-radius: 14px;
}

.gridPopup {
  right: auto;
  min-width: 100%;
  width: max-content;
  --cell: 72px;
}

.roundPopup {
  --cell: 40px;
}

.list {
  position: relative;
  max-height: 288px;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.pill {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 40px;
  border-radius: 12px;
  background: var(--pill-bg);
  box-shadow: inset 0 0 0 1px var(--pill-line);
  opacity: 0;
  pointer-events: none;
  transition:
    transform 0.26s cubic-bezier(0.2, 0.7, 0.2, 1),
    opacity 0.15s ease;
}

.option {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 12px;
  border-radius: 12px;
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.sm .option,
.sm .pill {
  height: 34px;
  border-radius: 10px;
}

.spark {
  margin-left: auto;
  width: 14px;
  height: 14px;
  color: var(--accent);
  opacity: 0;
}

.option.selected {
  color: var(--accent);
}

.option.selected .spark {
  opacity: 1;
}

.off {
  opacity: 0.4;
  pointer-events: none;
}

.head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 8px 10px 6px;
  font-size: 12px;
  color: var(--muted);
}

.headTitle {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 16px;
  color: var(--text);
}

.grid {
  display: grid;
  grid-template-columns: repeat(var(--columns), var(--cell, 1fr));
  gap: 4px;
  padding: 4px;
}

.cell {
  display: grid;
  place-items: center;
  height: 38px;
  border-radius: 12px;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  user-select: none;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.cell.round {
  height: auto;
  aspect-ratio: 1;
  border-radius: 999px;
}

.cell.active {
  background: var(--pill-bg);
  box-shadow: inset 0 0 0 1px var(--pill-line);
}

.cell.selected {
  background: var(--fill);
  background-image: var(--fill-gradient);
  color: var(--on-fill);
  box-shadow: 0 6px 18px var(--glow);
}

.sheetHost {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.scrim {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  animation: fade-in 0.2s ease;
}

.sheet {
  position: relative;
  padding: 10px 16px calc(18px + var(--inset-bottom, env(safe-area-inset-bottom, 0px)));
  border-radius: 26px 26px 0 0;
  background: color-mix(in srgb, var(--bg-raised) 96%, transparent);
  border-top: 1px solid var(--bar-line);
  box-shadow: var(--bar-shadow);
  -webkit-backdrop-filter: blur(22px) saturate(1.3);
  backdrop-filter: blur(22px) saturate(1.3);
  color: var(--text);
  font-size: 15px;
  animation: sheet-in 0.32s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.grab {
  width: 38px;
  height: 4px;
  margin: 0 auto 10px;
  border-radius: 4px;
  background: var(--muted-dim);
  opacity: 0.5;
}

.sheetTitle {
  margin: 0;
  text-align: center;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 20px;
}

.sheetHint {
  margin: 2px 0 0;
  text-align: center;
  font-size: 12px;
  color: var(--muted);
}

.sheet .grid {
  margin-top: 12px;
}

.sheet .cell {
  height: 46px;
}

.sheet .cell.round {
  height: auto;
}

@keyframes pop-in {
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
}

@keyframes sheet-in {
  from {
    transform: translateY(100%);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .popup,
  .sheet,
  .scrim {
    animation: none;
  }

  .pill,
  .chevron,
  .cell,
  .trigger {
    transition: none;
  }
}
